import React, { Ref } from 'react';
import { useSelector } from 'react-redux';

import AvtaleAttentionMessage, { AvtaleInfoMessageType } from '../../avtaler/avtale-info-message';
import {
    ForsikringButtons,
    OptionProps,
    selectedAlternativValue,
} from '../../components/dekning-buttons/forsikring-buttons';
import { InputTekst } from '../../components/input/input-text';
import { AppState } from '../../reducers';
import { DateAsString, ForsikringDekningDTO, TextInputSize } from '../../types/generated-types';
import { FocusableHtmlElement } from '../hooks';
import { updateForsikringSvar, updateSvar } from '../reducer/sporsmalsflyt-actions';
import { findSkadeDato } from '../reducer/sporsmalsflyt-state';
import { ForsikringAlternativ } from '../reducer/sporsmalsflyt-types';
import { useSporsmalsflytDispatch } from '../reducer/sporsmalsflytDispatchProvider';
import {
    CurrentForsikringIngenAlternativerData,
    CurrentForsikringListeData,
    CurrentForsikringsporsmal,
    isForsikringListe,
} from './forsikring-types';

import '../radio/radio.scss';

export function toOptions(alternativer: ForsikringAlternativ[]): OptionProps[] {
    return alternativer.map((alternative) => ({
        label: alternative.tekst,
        description: alternative.undertekst,
        value: selectedAlternativValue(alternative.forsikringsnummer, alternative.forsikringversjon),
        forsikringsnummer: alternative.forsikringsnummer,
        id: alternative.id + alternative.forsikringsnummer,
        versjon: alternative.forsikringversjon,
    }));
}

interface ForsikringListeProps {
    currentSporsmal: CurrentForsikringsporsmal;
    data: CurrentForsikringListeData;
    focusOnMount: boolean;
    skadedato: DateAsString | undefined;
    errorRef?: Ref<FocusableHtmlElement>;
}

const ForsikringListe = ({ currentSporsmal, data, focusOnMount, skadedato, errorRef }: ForsikringListeProps) => {
    const dispatch = useSporsmalsflytDispatch();

    const onChange = (value: string) => {
        const selectedAlternativ = data.alternativer.find(
            (alternativ) =>
                selectedAlternativValue(alternativ.forsikringsnummer, alternativ.forsikringversjon) === value,
        );
        dispatch(updateForsikringSvar(selectedAlternativ));
    };

    return (
        <div className="container__standard-width">
            <ForsikringButtons
                name={currentSporsmal.sporsmal.id}
                title={currentSporsmal.sporsmal.tekst}
                info={currentSporsmal.sporsmal.info}
                ingress={currentSporsmal.sporsmal.ingress}
                options={toOptions(data.alternativer)}
                selectedOption={data.selectedAlternativ}
                onChange={onChange}
                errorRef={errorRef}
                focusOnMount={focusOnMount}
                key={`radio-sporsmal-${currentSporsmal.sporsmal.id + currentSporsmal.sporsmal.tekst}`}
            />
            {data.selectedAlternativ?.id === ForsikringDekningDTO.FinnerIkkeRiktig && (
                <AvtaleAttentionMessage
                    avtaleInfoMessageType={AvtaleInfoMessageType.FinnerIkkeRiktig}
                    skadedato={skadedato}
                    componentToShowIfNoOtherAvtaler={null}
                    className="margin-top--medium"
                />
            )}
        </div>
    );
};

interface ForsikringSporsmalIngenAlternativerProps {
    currentSporsmal: CurrentForsikringsporsmal;
    data: CurrentForsikringIngenAlternativerData;
    focusOnMount: boolean;
    skadedato: DateAsString | undefined;
}

const ForsikringIngenAlternativerInput = ({
    currentSporsmal,
    data,
    focusOnMount,
    skadedato,
}: ForsikringSporsmalIngenAlternativerProps) => {
    const dispatch = useSporsmalsflytDispatch();

    const onChange = (value: string) => {
        dispatch(updateSvar(value));
    };

    return (
        <div className="container__standard-width">
            <AvtaleAttentionMessage
                avtaleInfoMessageType={AvtaleInfoMessageType.IngenForsikring}
                skadedato={skadedato}
                componentToShowIfNoOtherAvtaler={null}
                className="margin-bottom--medium"
            />
            <InputTekst
                tittel={currentSporsmal.sporsmal.ingenForsikringHeader}
                ingress={currentSporsmal.sporsmal.ingenForsikringTekst}
                verdi={data.forsikringsnummer}
                feilmelding=""
                isValid
                onChange={onChange}
                size={TextInputSize.ExtraSmall}
                focusOnMount={focusOnMount}
                id={`dekning-sporsmal-${currentSporsmal.sporsmal.id}`}
                key={`dekning-sporsmal-${currentSporsmal.sporsmal.id}`}
            />
        </div>
    );
};

interface ForsikringSporsmalWithAlternativerProps {
    currentSporsmal: CurrentForsikringsporsmal;
    focusOnMount: boolean;
    errorRef?: Ref<FocusableHtmlElement>;
}

export const Forsikring = ({ currentSporsmal, focusOnMount, errorRef }: ForsikringSporsmalWithAlternativerProps) => {
    const skademeldingState = useSelector((state: AppState) => state.skademelding);
    const { sporsmalsflytState } = skademeldingState;
    const skadedato = findSkadeDato(sporsmalsflytState.besvarteSporsmal);

    if (isForsikringListe(currentSporsmal.data)) {
        return (
            <ForsikringListe
                currentSporsmal={currentSporsmal}
                data={currentSporsmal.data}
                focusOnMount={focusOnMount}
                errorRef={errorRef}
                skadedato={skadedato}
            />
        );
    }
    return (
        <ForsikringIngenAlternativerInput
            currentSporsmal={currentSporsmal}
            data={currentSporsmal.data}
            focusOnMount={focusOnMount}
            skadedato={skadedato}
        />
    );
};
